import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import ReportsMonitoringContainer from '../../containers/reports/Monitoring';
import {
	AreaChart,
	Area,
	Legend,
	ResponsiveContainer,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
} from 'recharts';

//- material-ui
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

const styles = () => ({
	paper: {
		width: '100%',
		overflowX: 'auto',
		paddingBottom: 20,
	},
	row: {
		cursor: 'pointer',
	},
	button: {
		verticalAlign: 'middle',
		padding: '8px 10px',
	},
	formControl: {
		fontSize: 14,
		marginLeft: 20,
		flex: 1,
	},
	title: {
		display: 'flex',
		alignItems: 'flex-end',
	},
	label: {},
	value: {
		fontWeight: 600,
	},
	sectionHeader: {
		textTransform: 'uppercase',
		fontSize: 16,
		color: '#777',
		fontWeight: 900,
		letterSpacing: 2,
		marginTop: 30,
		marginBottom: 10,
	},
});

function getYearMonth({ date }) {
	return `${date.year()}_${date.month()}`;
}

class TotalVolume extends Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'asc',
			orderBy: 'number',
			search: '',
		};
		this.currentMonth = moment();
		this.lastMonth = moment().subtract(1, 'month');

		this.handleRowClick = this.handleRowClick.bind(this);
		this.handleRequestSort = this.handleRequestSort.bind(this);
		this.handleSearch = this.handleSearch.bind(this);
	}

	monthCheck({ date }) {
		const year = date.year();
		const month = date.month();
		const yearMonth = getYearMonth({ date });

		if (!this.props.salesByMonth[yearMonth]) {
			this.props.getMonthlySales({ params: { year, month, doNotShowInactive: true } });
		}
	}

	componentDidMount() {
		this.monthCheck({ date: this.currentMonth });
		this.monthCheck({ date: this.lastMonth });
	}

	handleSearch(event) {
		this.setState({ search: event.target.value });
	}

	handleRowClick(stationId, tankNumber) {
		this.props.history.push(`/reports/stationVolume/${stationId}/${tankNumber}`);
	}

	handleRequestSort(property) {
		const orderBy = property;
		let order = 'desc';

		if (this.state.orderBy === property && this.state.order === 'desc') {
			order = 'asc';
		}
		this.setState({ order, orderBy });
	}

	render() {
		const {
			classes,
			salesByMonth,
			stationMap,
			gasFormulaMap,
			rackFormulaMap,
			authUser,
		} = this.props;

		if (!gasFormulaMap || !rackFormulaMap || !authUser || !stationMap) {
			return null;
		}

		const currentYearMonth = getYearMonth({ date: this.currentMonth });
		const lastYearMonth = getYearMonth({ date: this.lastMonth });

		const currentMonthData = salesByMonth[currentYearMonth];
		const lastMonthData = salesByMonth[lastYearMonth];

		if (!currentMonthData || !lastMonthData) {
			return <div>Calculating monthly sales...</div>;
		}

		//
		//- All the data is here render
		//

		const totals = [];
		const forLegend = [];
		let byDateForChart = {};
		[
			{ type: 'Current', data: currentMonthData, date: this.currentMonth, runningTotal: 0 },
			{ type: 'Prior', data: lastMonthData, date: this.lastMonth, runningTotal: 0 },
		].forEach((o) => {
			const monthKey = o.date.format('MMMM');
			forLegend.push({ name: monthKey });

			/** @namespace o.data.monthlyProductSales **/
			/** @namespace e85Data.averageDailyAverage **/
			/** @namespace e85Data.averageDailyVolume **/
			/** @namespace e85Data.totalVolume **/
			/** @namespace e85Data.dailyTotals **/

			const e85Data = _.find(o.data.monthlyProductSales, { productType: 'e85' });
			if (!e85Data) {
				console.error('Missing e85 data for some reason', o.data);
				return;
			}

			totals.push({
				type: o.type,
				header: `${o.type} Month E85 Volume`,
				stats: [
					{
						label: `Average per station daily average`,
						value: _.round(e85Data.averageDailyAverage, 1),
					},
					{
						label: `Average daily sales across all stations`,
						value: _.round(e85Data.averageDailyVolume, 0).toLocaleString(),
					},
					{
						label: `Total across all stations for month`,
						value: _.round(e85Data.totalVolume, 0).toLocaleString(),
					},
					{
						label: `Based on # of stations / tanks`,
						value: _.round(e85Data.tanks, 1),
					},
				],
			});

			const dailyTotals = e85Data.dailyTotals;

			let i = 0;
			const byDate = _.keyBy(dailyTotals, (r) => {
				r[monthKey] = r.totalVolume || null;
				if (r.totalVolume) {
					o.runningTotal += r.totalVolume;
					o.runningAverage = o.runningTotal / ++i;
				} else {
					o.runningTotal = null;
					o.runningAverage = null;
				}
				r[`${monthKey}_runningTotal`] = o.runningTotal;
				r[`${monthKey}_runningAverage`] = _.round(o.runningAverage);
				const date = moment(r.date);
				r.name = date.date();
				return date.date();
			});

			byDateForChart = _.merge(byDateForChart, byDate);
		});
		const dailySalesChartData = _.sortBy(_.values(byDateForChart), 'name');
		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<div>
						<Grid container spacing={3}>
							<Grid item xs={8}>
								<Typography variant="h5" align="left" gutterBottom className={classes.title}>
									<span>E85 Volume Totals</span>
								</Typography>
							</Grid>
						</Grid>

						<Paper className={classes.paper}>
							<div style={{ padding: 20, textAlign: 'left' }}>
								{totals.map((total) => {
									return (
										<div key={total.type}>
											<div className={classes.sectionHeader}>{total.header}</div>
											<div>
												{total.stats.map((stat, i) => {
													return (
														<Grid key={i} container spacing={2}>
															<Grid item xs={3} style={{ textAlign: 'left' }}>
																<span className={classes.label}>{stat.label}</span>
															</Grid>
															<Grid item xs={2} style={{ textAlign: 'right' }}>
																<span className={classes.value}>{stat.value}</span>
															</Grid>
														</Grid>
													);
												})}
											</div>
										</div>
									);
								})}
							</div>
						</Paper>

						<Grid container spacing={3} style={{ paddingTop: 30 }}>
							<Grid item xs={8}>
								<Typography variant="h5" align="left" gutterBottom className={classes.title}>
									<span>E85 Monthly Graphs</span>
								</Typography>
							</Grid>
						</Grid>

						<Paper className={classes.paper}>
							<div style={{ padding: 20 }}>
								Running Average Sales Across All Stations by Day
								<ResponsiveContainer width="100%" height={300}>
									<AreaChart
										data={dailySalesChartData}
										margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
									>
										<defs>
											<linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
												<stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
												<stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
											</linearGradient>
											<linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
												<stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
												<stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
											</linearGradient>
										</defs>
										<XAxis dataKey="name" />
										<YAxis domain={[5000, 'auto']} />
										<CartesianGrid strokeDasharray="3 3" />
										<Tooltip />
										<Legend verticalAlign="top" height={36} />
										<Area
											type="monotone"
											name={forLegend[0].name}
											dataKey={`${forLegend[0].name}_runningAverage`}
											stroke="#82ca9d"
											fillOpacity={1}
											fill="url(#colorPv)"
										/>
										<Area
											type="monotone"
											name={forLegend[1].name}
											dataKey={`${forLegend[1].name}_runningAverage`}
											stroke="#8884d8"
											fillOpacity={1}
											fill="url(#colorUv)"
										/>
									</AreaChart>
								</ResponsiveContainer>
							</div>

							<div style={{ padding: 20 }}>
								Total Sales Across All Stations by Day
								<ResponsiveContainer width="100%" height={300}>
									<AreaChart
										data={dailySalesChartData}
										margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
									>
										<defs>
											<linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
												<stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
												<stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
											</linearGradient>
											<linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
												<stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
												<stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
											</linearGradient>
										</defs>
										<XAxis dataKey="name" />
										<YAxis domain={[3000, 'auto']} />
										<CartesianGrid strokeDasharray="3 3" />
										<Tooltip />
										<Legend verticalAlign="top" height={36} />
										<Area
											type="monotone"
											dataKey={forLegend[0].name}
											stroke="#82ca9d"
											fillOpacity={1}
											fill="url(#colorPv)"
										/>
										<Area
											type="monotone"
											dataKey={forLegend[1].name}
											stroke="#8884d8"
											fillOpacity={1}
											fill="url(#colorUv)"
										/>
									</AreaChart>
								</ResponsiveContainer>
							</div>
						</Paper>
					</div>
				</Grid>
			</Grid>
		);
	}
}

TotalVolume.propTypes = {
	classes: PropTypes.object.isRequired,
	getMonthlySales: PropTypes.func.isRequired,
	salesByMonth: PropTypes.object,
	stationMap: PropTypes.object,
	rackFormulaMap: PropTypes.object,
	gasFormulaMap: PropTypes.object,
	order: PropTypes.string,
	orderBy: PropTypes.string,
	history: PropTypes.object,
	authUser: PropTypes.object,
};

export default _.flow([withStyles(styles), ReportsMonitoringContainer])(TotalVolume);
